import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum.ts';
import { Route } from '@/Enums/Routes/Route.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import CampaignExportButton from '@/components/Buttons/CampaignExportButton/index.tsx';
import DuplicateButton from '@/components/Buttons/DuplicateButton/index.tsx';
import NoDataCard from '@/components/NotFound/NoDataCard';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { lineItemFormToApiCreate, lineItemFormToApiUpdate } from '@/helpers/campaignManagement';
import { campaignFormDataToApiStructure } from '@/helpers/campaignManagement/campaignForm';
import {
  dv360TicketsLineItemsFormToApiCreate,
  dv360TicketsLineItemsFormToApiUpdate,
} from '@/helpers/dv360Tickets/campaignManagement/lineItemForm';
import { isCreativeAssociationException } from '@/helpers/exceptions';
import { useCampaignExport } from '@/hooks/useCampaignExport';
import DV360LineItemForm from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/index.tsx';
import { useAppSelector } from '@/store/hooks';
import { selectCampaignManagementProducts } from '@/store/selectors/campaignManagement.selectors';
import { selectDV360TicketsActiveInsertionOrder } from '@/store/selectors/dv360Tickets/insertionOrders.selectors.ts';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import {
  createCampaign,
  createLineItem,
  getCampaignData,
  getProducts,
  resetActiveCampaign,
  updateCampaign,
  updateLineItem,
  uploadLineItemDomainLists,
} from '@/store/slices/campaignManagementSlice';
import {
  createDV360TicketsInsertionOrder,
  createDV360TicketsLineItem,
  getDv360TicketsInsertionOrderData,
  updateDV360TicketsInsertionOrder,
  updateDV360TicketsLineItem,
} from '@/store/slices/dv360Tickets/insertionOrderManagement.slice.ts';
import { getDealGroups, getPackages } from '@/store/slices/sharedApi.slice';
import { Add } from '@mui/icons-material';
import { Button, Skeleton } from '@mui/material';
import Paper from '@mui/material/Paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CampaignForm from './CampaignForm';
import LineItemForm from './LineItemForm';
import CreateCampaignBreadcrumbs from './components/CreateCampaignBreadcrumbs';
import DuplicateCampaignModal from './components/DuplicateCampaignModal';
import Errors from './components/Errors';
import RequireApprovalBanner from './components/RequireApprovalBanner';

const normalizeLineItemName = n => n?.trim().replace(/\s+/g, ' ').toLowerCase() ?? '';

const CreateCampaign = () => {
  const { campaignId, idType } = useParams();

  const [searchParams] = useSearchParams();
  const lineItemId = searchParams.get('lineItemId');

  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const isDV360Id = useMemo(() => idType === 'dv360', [idType]);
  const isNew = useMemo(() => campaignId === NEW_ITEM_ID, [campaignId]);

  const activeBWCampaign = useAppSelector(({ campaignManagement }) => campaignManagement.activeCampaign);
  const activeBWCampaignLoading = useAppSelector(
    ({ campaignManagement }) => !!campaignManagement.activeCampaignRequestId,
  );
  const activeBWCampaignError = useAppSelector(({ campaignManagement }) => campaignManagement.activeCampaignError);

  const {
    activeInsertionOrder,
    loading: insertionOrderLoading,
    activeInsertionOrderError,
  } = useAppSelector(selectDV360TicketsActiveInsertionOrder);

  const activeCampaign = useMemo(() => {
    return isDV360Id ? activeInsertionOrder : activeBWCampaign;
  }, [isDV360Id, activeBWCampaign, activeInsertionOrder]);

  const activeCampaignLoading = useMemo(() => {
    return isDV360Id ? insertionOrderLoading : activeBWCampaignLoading;
  }, [isDV360Id, insertionOrderLoading, activeBWCampaignLoading]);

  const activeCampaignError = useMemo(() => {
    if (isNew) return false;
    return isDV360Id ? activeInsertionOrderError : activeBWCampaignError;
  }, [isNew, isDV360Id, activeInsertionOrderError, activeBWCampaignError]);

  const isUserReadOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CAMPAIGN_MANAGEMENT));

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { products: productsWithPublishers } = useAppSelector(selectCampaignManagementProducts);

  const advertiserId = useMemo(() => activeCampaign?.advertiser?.id, [activeCampaign]);

  const loadCampaign = useCallback(() => {
    if (isDV360Id) {
      dispatch(
        getDv360TicketsInsertionOrderData({
          id: campaignId,
        }),
      );
    } else {
      dispatch(
        getCampaignData({
          campaignId,
        }),
      );
    }
  }, [dispatch, campaignId, isDV360Id]);

  useEffect(() => {
    if (!productsWithPublishers && advertiserId) {
      dispatch(getProducts({ advertiserId }));
    }
  }, [dispatch, productsWithPublishers, advertiserId]);

  useEffect(() => () => dispatch(resetActiveCampaign()), [dispatch]);

  useEffect(() => {
    if (campaignId && !isNew) {
      loadCampaign();
    }
  }, [campaignId, dispatch, loadCampaign]);

  useEffect(() => {
    dispatch(getDealGroups());
    dispatch(getPackages());
  }, [dispatch]);

  const handleCreateCampaign = useCallback(
    async (payload, isGooglePlatform) => {
      if (isGooglePlatform) {
        return dispatch(createDV360TicketsInsertionOrder(payload)).unwrap();
      }

      return dispatch(createCampaign(payload)).unwrap();
    },
    [dispatch],
  );

  const handleUpdateCampaign = useCallback(
    async (payload, isGooglePlatform) => {
      if (isGooglePlatform) {
        return dispatch(updateDV360TicketsInsertionOrder(payload)).unwrap();
      }

      return dispatch(updateCampaign(payload)).unwrap();
    },
    [dispatch],
  );

  const handleCampaignSave = useCallback(
    async (formData, platformType) => {
      try {
        if (isUserReadOnly) {
          toast.error(`You are not allowed to ${isNew ? 'create' : 'update'} Campaigns`);
          return;
        }

        const isGooglePlatform = platformType === CampaignPlatformType.Google;

        if (isNew) {
          const payload = campaignFormDataToApiStructure(formData);
          const { id } = await handleCreateCampaign(payload, isGooglePlatform);

          const route = isGooglePlatform
            ? `${Route.CAMPAIGN_MANAGEMENT}/dv360/${id}`
            : `${Route.CAMPAIGN_MANAGEMENT}/${id}`;

          navigate(route);
        } else {
          const payload = { id: campaignId, name: formData.name, altId: formData.altId };
          await handleUpdateCampaign(payload, isGooglePlatform);
        }

        toast.success('Campaign saved');
      } catch (err) {
        toast.error(`Failed to save Campaign: ${err.message}`);
      }
    },
    [campaignId, dispatch, isNew, isUserReadOnly, navigate],
  );

  const [lineItemsIds, setLineItemsIds] = useState([]);
  const [createdLineItemId, setCreatedLineItemId] = useState();

  useEffect(() => {
    setLineItemsIds(activeCampaignLoading ? [] : activeCampaign?.lineItems?.map(({ id }) => id) || []);
  }, [activeCampaign, activeCampaignLoading]);

  const handleAddLineItem = () => {
    if (isNew) {
      toast.info('Create Campaign first');
      return;
    }

    if (lineItemsIds.at(-1) === NEW_ITEM_ID) {
      toast.info('Save previous Line Item first');
      return;
    }

    setLineItemsIds([...lineItemsIds, NEW_ITEM_ID]);
  };

  const createLineItemSaveHandler = useCallback(
    isDV360 =>
      id =>
      async ({ formData, changedValues }) => {
        const isCreate = id === NEW_ITEM_ID;

        if (isUserReadOnly) {
          toast.error(`You are not allowed to ${isCreate ? 'create' : 'update'} Line Items`);
          return;
        }

        const nonArchivedLineItems = activeCampaign?.lineItems?.filter(({ is_archived }) => !is_archived) ?? [];
        const duplicates = formData.duplicates ?? [];
        const isDuplication = duplicates.length > 0;

        if (isDuplication) {
          const existingNames = new Set(nonArchivedLineItems.map(({ name }) => normalizeLineItemName(name)));
          const dupNames = duplicates.map(d => normalizeLineItemName(d.name));
          const hasDupNameConflict =
            new Set(dupNames).size !== dupNames.length || dupNames.some(n => existingNames.has(n));
          if (hasDupNameConflict) {
            toast.error('Line Item with the same name already exists in this campaign');
            return;
          }
        } else {
          const hasLineItemWithSameName = nonArchivedLineItems
            .filter(({ id: liId }) => isCreate || liId !== id)
            .some(({ name }) => normalizeLineItemName(name) === normalizeLineItemName(formData.name));
          if (hasLineItemWithSameName) {
            toast.error('Line Item with the same name already exists in this campaign');
            return;
          }
        }

        try {
          const hasCustomDomains =
            !isDV360 &&
            (formData.hasCustomDomainLists ||
              formData.customAllowDomains?.length > 0 ||
              formData.customBlockDomains?.length > 0);

          if (isCreate) {
            const preparedData = isDV360
              ? dv360TicketsLineItemsFormToApiCreate(campaignId, formData)
              : lineItemFormToApiCreate(campaignId, formData);

            const createAction = isDV360 ? createDV360TicketsLineItem : createLineItem;
            const { id: lineItemId } = await dispatch(createAction(preparedData)).unwrap();
            setCreatedLineItemId(lineItemId);

            if (hasCustomDomains) {
              await dispatch(
                uploadLineItemDomainLists({
                  lineItemId,
                  allowDomains: formData.customAllowDomains ?? [],
                  blockDomains: formData.customBlockDomains ?? [],
                }),
              ).unwrap();
            }
          } else {
            const updateValues = hasCustomDomains ? { ...changedValues, channel: formData.channel } : changedValues;
            const preparedData = isDV360
              ? dv360TicketsLineItemsFormToApiUpdate(campaignId, updateValues)
              : lineItemFormToApiUpdate(campaignId, updateValues);

            const updateAction = isDV360 ? updateDV360TicketsLineItem : updateLineItem;
            await dispatch(updateAction({ id, ...preparedData })).unwrap();

            if (hasCustomDomains) {
              await dispatch(
                uploadLineItemDomainLists({
                  lineItemId: id,
                  allowDomains: formData.customAllowDomains ?? [],
                  blockDomains: formData.customBlockDomains ?? [],
                }),
              ).unwrap();
            }
          }

          toast.success('Line Item saved');
          return true;
        } catch (error) {
          if (isCreativeAssociationException(error)) {
            toast.success(isCreate ? 'Line Item created' : 'Line Item updated');
            toast.error(error?.message || 'Line Item Creative association failed');
            if (isCreate) {
              // force reload campaign to get full created line item
              loadCampaign();
            }
            return true;
          }
          toast.error(error?.message || 'Failed to save Line Item');
          return false;
        }
      },
    [campaignId, dispatch, isUserReadOnly, activeCampaign, loadCampaign],
  );

  const handleLineItemSave = useMemo(() => createLineItemSaveHandler(false), [createLineItemSaveHandler]);

  const handleDV360LineItemSave = useMemo(() => createLineItemSaveHandler(true), [createLineItemSaveHandler]);

  const { exportCampaign, canExport } = useCampaignExport(isDV360Id ? null : activeCampaign);

  const onClickDuplicate = useCallback(() => setIsDuplicationModalOpen(true), []);
  const onClose = useCallback(() => setIsDuplicationModalOpen(false), []);

  if (activeCampaignError) {
    return <NoDataCard campaignId={campaignId} renderContent={Errors[activeCampaignError]} sx={{ height: '100%' }} />;
  }

  return (
    <>
      <RequireApprovalBanner />
      <div className='w-full space-y-[30px] p-[30px] bg-[#FAFAFA] h-screen overflow-y-auto'>
        <div className='justify-between items-center '>
          <div className='flex justify-between items-center'>
            <CreateCampaignBreadcrumbs isNew={isNew} readOnly={isUserReadOnly} />
            <div className='flex items-center gap-2'>
              {!isDV360Id && (
                <CampaignExportButton
                  onClick={exportCampaign}
                  disabled={isNew || activeCampaignLoading || !canExport}
                />
              )}
              <DuplicateButton onClick={onClickDuplicate} disabled={isNew} />
            </div>
          </div>
          <CampaignForm
            onSave={handleCampaignSave}
            readOnly={isUserReadOnly}
            loading={activeCampaignLoading}
            isNew={isNew}
            isDV360={isDV360Id}
          />
          {activeCampaignLoading ? (
            <Skeleton variant='rounded' height={100} sx={{ marginTop: 4, borderRadius: 2 }} />
          ) : (
            <>
              {isDV360Id
                ? lineItemsIds.map((id, index) => (
                    <DV360LineItemForm
                      key={id}
                      id={id}
                      index={index}
                      defaultExpanded={id === NEW_ITEM_ID || id === createdLineItemId || id === +lineItemId}
                      onSave={handleDV360LineItemSave(id)}
                      readOnly={isUserReadOnly}
                    />
                  ))
                : lineItemsIds.map((id, index) => (
                    <LineItemForm
                      key={id}
                      id={id}
                      index={index}
                      defaultExpanded={id === NEW_ITEM_ID || id === createdLineItemId || id === +lineItemId}
                      onSave={handleLineItemSave(id)}
                      readOnly={isUserReadOnly}
                    />
                  ))}
              <Paper className='py-7 flex justify-center mt-8 bg-white'>
                <Button
                  className='flex items-center gap-1 font-semibold'
                  onClick={handleAddLineItem}
                  sx={{
                    fontSize: 14,
                    displayPrint: 'none',
                    color: '#020202',
                  }}
                >
                  <Add />
                  <span className='text-xl'>Add Line Item</span>
                </Button>
              </Paper>
            </>
          )}
        </div>
      </div>
      <DuplicateCampaignModal
        isOpen={isDuplicationModalOpen}
        handleClose={onClose}
        originalCampaignName={activeCampaign?.name || ''}
        campaignId={activeCampaign?.id}
        isDV360={isDV360Id}
      />
    </>
  );
};

export default CreateCampaign;
